import { Box, Checkbox, Grid, Stack, Typography } from "@mui/material";
import {
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
  type GridRowId,
} from "@mui/x-data-grid-premium";
import { useSelector } from "react-redux";
import { convertISODateShort } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import { getEntityFromMessage, getFundsFromMessage } from "../../../../store/state/messages/messageHelper";
import type { Message } from "../../../../store/state/messages/types";
import { impersonationSelector } from "../../../../store/state/user/selectors";
import MessageCategoryTag from "../../MessageCategoryTag";
import InlineItemsWithBadge from "../InlineItemsWithBadge";
import MessagePublishStatusIcon from "../MessagePublishStatusIcon";
import MessageTypeComponent from "../MessageType";

interface ItemTextProps {
  text: string;
  emphasize: boolean;
}

const ItemText = ({ text, emphasize }: ItemTextProps) => {
  return (
    <Typography variant="subtitle2" noWrap sx={{ fontWeight: emphasize ? "bold" : "normal" }}>
      {text}
    </Typography>
  );
};

interface Props {
  message: Message;
  id: GridRowId;
}

const MessageListViewCell = ({ message, id }: Props) => {
  const apiRef = useGridApiContext();
  const selectedIds = useGridSelector(apiRef, gridRowSelectionIdsSelector);

  const isImpersonation = useSelector(impersonationSelector);

  const isSelected = selectedIds.has(id);

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Checkbox
        checked={isSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          apiRef.current.selectRow(id, e.target.checked);
        }}
        sx={{ px: 1.75, svg: { fontSize: "1.25rem" } }}
      />
      <Stack spacing={0.5} sx={{ width: "calc(100% - 40px)", flex: 1, py: 1, pr: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ width: "100%", display: "flex", flex: 1, justifyContent: "space-between", alignItems: "center" }}>
          <InlineItemsWithBadge
            items={getFundsFromMessage(message)}
            subItem={getEntityFromMessage(message)}
            fontWeight={message.isRead ? "normal" : "bold"}
          />
          <ItemText emphasize={!message.isRead} text={convertISODateShort(message.sendDate)} />
        </Box>

        <Grid container spacing={1} sx={{ width: "100%", alignItems: "center" }}>
          <Grid size={9}>
            <ItemText emphasize={!message.isRead} text={message.content.subject} />
          </Grid>
          <Grid size={3}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
              <MessageTypeComponent message={message} />
            </Box>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ width: "100%", alignItems: "center" }}>
          <MessageCategoryTag categoryId={message.categoryId} />
          {isImpersonation && (
            <>
              <MessagePublishStatusIcon publishingStatus={message.publishingStatus} />
              <ItemText emphasize={!message.isRead} text={autoFormatCamelCase(message.publishingStatus)} />
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default MessageListViewCell;
